/**
 * @description API helper for company holiday calendar (LYN-103)
 *
 * Key features:
 * - List platform US holidays (with optional observedDate for a year)
 * - List company holidays by year/range
 * - Enable federal / bulk-enable / add custom / patch pay flags
 *
 * @dependencies
 * - apiClient: Axios instance with auth + X-Company-Id
 *
 * @notes
 * - Maps only to company_holidays / platform_holidays fields from LYN-98
 * - Does not invent time_clock.holiday_* settings keys
 */

import apiClient from './apiClient';

const holidaysService = {
  /**
   * @param {{ countryCode?: string, year?: number, includeInactive?: boolean }} [params]
   * @returns {Promise<{holidays: Array}>}
   */
  listPlatformHolidays: async (params = {}) => {
    const response = await apiClient.get('/holidays/platform', { params });
    return response.data;
  },

  /**
   * @param {{ year?: number, startDate?: string, endDate?: string, date?: string, includeInactive?: boolean }} [params]
   * @returns {Promise<{holidays: Array}>}
   */
  listCompanyHolidays: async (params = {}) => {
    const response = await apiClient.get('/holidays', { params });
    return response.data;
  },

  /**
   * @param {{ platformHolidayId: string, year?: number, employeePaid?: boolean, premiumMultiplier?: number, notes?: string }} body
   * @returns {Promise<{holiday: object}>}
   */
  enablePlatformHoliday: async (body) => {
    const response = await apiClient.post('/holidays/enable', body);
    return response.data;
  },

  /**
   * @param {{ year?: number, countryCode?: string, employeePaid?: boolean, premiumMultiplier?: number }} [body]
   * @returns {Promise<{holidays: Array}>}
   */
  enableAllPlatformHolidays: async (body = {}) => {
    const response = await apiClient.post('/holidays/enable-all', body);
    return response.data;
  },

  /**
   * @param {{ name: string, observedDate: string, countryCode?: string, employeePaid?: boolean, premiumMultiplier?: number, notes?: string }} body
   * @returns {Promise<{holiday: object}>}
   */
  addCustomHoliday: async (body) => {
    const response = await apiClient.post('/holidays/custom', body);
    return response.data;
  },

  /**
   * @param {string} holidayId
   * @param {{ employeePaid?: boolean, premiumMultiplier?: number, isActive?: boolean, notes?: string }} patch
   * @returns {Promise<{holiday: object}>}
   */
  updateCompanyHoliday: async (holidayId, patch) => {
    const response = await apiClient.patch(`/holidays/${holidayId}`, patch);
    return response.data;
  },
};

export default holidaysService;
